import { reactive, computed } from 'vue';
import z from 'zod';
import isNumeric from 'validator/lib/isNumeric';
import { policyOverrideService, policyService } from '@/services/api';
import { PolicyOverride, PolicyLineOverride, OriginalValues } from '@/types/PolicyOverrides';
import { policyFieldLabels } from '@/components/v2/policy/policyFieldLabels';
import { compactMoney } from '@/utilities/Money';
import {
  subitemKindLabels,
  type PolicySublimit,
  type PolicySubretention,
  type SubitemKind,
  type SubitemTypeRef,
} from '@/types/PolicySublimit';

export type { SubitemKind };

type SubitemStatus = 'unchanged' | 'added' | 'updated' | 'deleted';

// One sublimit/subretention row as the panel renders it: a saved item, a staged addition, or a
// saved item with a staged edit or removal. `savedValue` is absent on a staged add; `originalValue`
// is absent on any user-created item, which is what distinguishes it from an AI/Epic-sourced one.
export interface SubitemRow {
  rowKey: string;
  kind: SubitemKind;
  itemId?: number;
  typeId: number;
  name: string;
  value: string;
  basis: string;
  savedValue?: number;
  savedBasis?: string;
  originalValue?: number;
  status: SubitemStatus;
}

interface StagedSubitem {
  kind: SubitemKind;
  op: 'add' | 'update' | 'delete';
  rowKey: string;
  itemId?: number;
  typeId?: number;
  typeName?: string;
  value?: string;
  basis?: string;
}

export type RecordKey = 'policy' | `line:${string}`;

export interface RecordRef {
  key: RecordKey;
  type: 'policy' | 'line';
  uniqPolicy: string;
  uniqLine?: string;
  label: string;
  subLabel?: string;
}

const numericString = z
  .string()
  .trim()
  .refine((val) => val === '' || isNumeric(val), { message: 'Must be a number' })
  .default('');

export const formSchema = z.object({
  peFlag: z.string().trim().default(''),
  attachmentPointAmount: numericString,
  attachmentApplicable: z.boolean().default(true),
  aggregateLimitAmount: numericString,
  aggregateLimitApplicable: z.boolean().default(true),
  occurrenceLimitAmount: numericString,
  occurrenceLimitApplicable: z.boolean().default(true),
  quotaShareTotal: numericString,
  retentionAmount: numericString,
  retentionApplicable: z.boolean().default(true),
  leadSyndicateId: z.string().trim().default(''),
  sideAValue: z.string().trim().default(''),
  broker: z.string().trim().default(''),
  runoff: z.boolean().default(false),
  carrierId: numericString,
  premiumTotal: numericString,
});

export type FormSchemeType = z.infer<typeof formSchema>;

export type ValidFormFields = { [K in keyof FormSchemeType]: boolean };

// Client-readiness rule flags for the record being edited; the form panel
// marks the corresponding fields until the violation is resolved.
export interface PolicyRuleViolationFlags {
  noLimit?: boolean;
  invalidRetention?: boolean;
  invalidAttachment?: boolean;
}

const allFieldsValid = (): ValidFormFields => ({
  peFlag: true,
  attachmentPointAmount: true,
  attachmentApplicable: true,
  aggregateLimitAmount: true,
  aggregateLimitApplicable: true,
  occurrenceLimitAmount: true,
  occurrenceLimitApplicable: true,
  quotaShareTotal: true,
  retentionAmount: true,
  retentionApplicable: true,
  leadSyndicateId: true,
  sideAValue: true,
  broker: true,
  runoff: true,
  carrierId: true,
  premiumTotal: true,
});

export interface DiffEntry {
  field: string;
  label: string;
  oldValue: string;
  newValue: string;
  // Optional: when a change is restricted (e.g. policy-source restriction in the AI
  // review flow) it is shown struck-through with a reason. Unused by the manual edit flow.
  restricted?: boolean;
  restrictionReason?: string;
}

interface RecordState {
  ref: RecordRef;
  loaded: boolean;
  form: FormSchemeType;
  originalForm: FormSchemeType;
  validFields: ValidFormFields;
  // The carrier override as loaded (group name, or undefined). Carried forward on save when the
  // user didn't change the carrier, since carrier resolution is lossy (group -> primary issuing
  // company) and re-sending the displayed carrier could normalize it to a different company.
  loadedCarrierOverride?: string;
  sublimits: PolicySublimit[];
  subretentions: PolicySubretention[];
  sublimitTypes: SubitemTypeRef[];
  subretentionTypes: SubitemTypeRef[];
  subitemTypesLoaded: boolean;
  stagedSubitems: StagedSubitem[];
}

// Carrier/premium overrides are only writable for certain policy sources, and the carrier
// Select stores an rInsurerGroup ID while the override stores the group name — so these caps
// carry the id<->name lookup and the source's edit permissions. Configured via configureCaps().
interface PolicyCaps {
  carrierIdToName: Map<string, string>;
  carrierNameToId: Map<string, string>;
  allowCarrierUpdate: boolean;
  allowPremiumUpdate: boolean;
}

function toForm(originals: Partial<OriginalValues>, overrides: Partial<PolicyOverride>, carrierNameToId: Map<string, string>): FormSchemeType {
  const peFlag = overrides.peFlag || originals.peFlag || '';
  const hasRetention = !!(overrides.retentionOverride || originals.retention);
  const hasAttachment = !!(overrides.attachmentOverride || originals.attachment);

  return {
    peFlag,
    attachmentPointAmount: overrides.attachmentOverride?.toString() || originals.attachment?.toString() || '',
    attachmentApplicable: peFlag === 'Primary' && !hasAttachment ? false : overrides.noAttachment === undefined ? true : !overrides.noAttachment,
    aggregateLimitAmount: overrides.aggregateLimitOverride?.toString() || originals.aggregateLimit?.toString() || '',
    aggregateLimitApplicable: overrides.noLimit === undefined ? true : !overrides.noLimit,
    occurrenceLimitAmount: overrides.occurrenceLimitOverride?.toString() || originals.occurrenceLimit?.toString() || '',
    occurrenceLimitApplicable: overrides.noOccurrenceLimit === undefined ? true : !overrides.noOccurrenceLimit,
    quotaShareTotal: overrides.quotaShareTotalOverride?.toString() || originals.quotaShareTotal?.toString() || '',
    retentionAmount: overrides.retentionOverride?.toString() || originals.retention?.toString() || '',
    retentionApplicable: peFlag === 'Excess' && !hasRetention ? false : overrides.noRetention === undefined ? true : !overrides.noRetention,
    leadSyndicateId: overrides.syndicateId?.toString() || originals.syndicateId?.toString() || '',
    sideAValue: overrides.sideAOverride ?? originals.sideA ?? '',
    broker: '',
    runoff: overrides.runoffOverride ?? originals.runoff === 'Runoff',
    carrierId: carrierNameToId.get((overrides.carrierOverride || originals.carrier || '').trim()) ?? '',
    premiumTotal: overrides.policyPremiumOverride?.toString() || originals.policyPremium?.toString() || '',
  } as FormSchemeType;
}

function toPolicyOverride(form: FormSchemeType, caps: PolicyCaps): PolicyOverride {
  return {
    peFlag: form.peFlag ?? '',
    attachmentOverride: form.attachmentPointAmount && form.attachmentApplicable ? Number(form.attachmentPointAmount) : undefined,
    noAttachment: !form.attachmentApplicable,
    aggregateLimitOverride: form.aggregateLimitAmount && form.aggregateLimitApplicable ? Number(form.aggregateLimitAmount) : undefined,
    noLimit: !form.aggregateLimitApplicable,
    occurrenceLimitOverride: form.occurrenceLimitAmount && form.occurrenceLimitApplicable ? Number(form.occurrenceLimitAmount) : undefined,
    noOccurrenceLimit: !form.occurrenceLimitApplicable,
    quotaShareTotalOverride: form.quotaShareTotal !== '' ? Number(form.quotaShareTotal) : undefined,
    retentionOverride: form.retentionAmount && form.retentionApplicable ? Number(form.retentionAmount) : undefined,
    noRetention: !form.retentionApplicable,
    syndicateId: form.leadSyndicateId ? Number(form.leadSyndicateId) : undefined,
    sideAOverride: form.sideAValue,
    runoffOverride: form.runoff,
    // Carrier/premium are only sent when the source permits it — otherwise the API's
    // validateCanUpdateCarrier/Premium gates reject the whole save.
    carrierOverride: caps.allowCarrierUpdate && form.carrierId ? caps.carrierIdToName.get(form.carrierId) : undefined,
    policyPremiumOverride: caps.allowPremiumUpdate && form.premiumTotal !== '' ? Number(form.premiumTotal) : undefined,
  } as PolicyOverride;
}

function toPolicyLineOverride(form: FormSchemeType): PolicyLineOverride {
  return {
    attachmentOverride: form.attachmentPointAmount && form.attachmentApplicable ? Number(form.attachmentPointAmount) : undefined,
    noAttachment: !form.attachmentApplicable,
    aggregateLimitOverride: form.aggregateLimitAmount && form.aggregateLimitApplicable ? Number(form.aggregateLimitAmount) : undefined,
    noLimit: !form.aggregateLimitApplicable,
    occurrenceLimitOverride: form.occurrenceLimitAmount && form.occurrenceLimitApplicable ? Number(form.occurrenceLimitAmount) : undefined,
    noOccurrenceLimit: !form.occurrenceLimitApplicable,
    quotaShareTotalOverride: form.quotaShareTotal !== '' ? Number(form.quotaShareTotal) : undefined,
    retentionOverride: form.retentionAmount && form.retentionApplicable ? Number(form.retentionAmount) : undefined,
    noRetention: !form.retentionApplicable,
    policyPremiumOverride: form.premiumTotal !== '' ? Number(form.premiumTotal) : undefined,
  };
}

function isEqual(a: FormSchemeType, b: FormSchemeType): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

interface FieldChange {
  field: string;
  newValueRaw: string | number | boolean | undefined;
  oldValueRaw: string | number | boolean | undefined;
}

function diffPolicyFields(form: FormSchemeType, original: FormSchemeType, mode: 'policy' | 'line'): FieldChange[] {
  const changes: FieldChange[] = [];

  const pushIfChanged = (field: string, newVal: unknown, oldVal: unknown) => {
    if (newVal !== oldVal) {
      changes.push({
        field,
        newValueRaw: newVal as FieldChange['newValueRaw'],
        oldValueRaw: oldVal as FieldChange['oldValueRaw'],
      });
    }
  };

  if (mode === 'policy') {
    pushIfChanged('peFlag', form.peFlag, original.peFlag);
  }

  const attachmentNew = form.attachmentApplicable ? form.attachmentPointAmount : '';
  const attachmentOld = original.attachmentApplicable ? original.attachmentPointAmount : '';
  pushIfChanged('attachmentOverride', attachmentNew, attachmentOld);

  const aggregateNew = form.aggregateLimitApplicable ? form.aggregateLimitAmount : '';
  const aggregateOld = original.aggregateLimitApplicable ? original.aggregateLimitAmount : '';
  pushIfChanged('aggregateLimitOverride', aggregateNew, aggregateOld);

  const occurrenceNew = form.occurrenceLimitApplicable ? form.occurrenceLimitAmount : '';
  const occurrenceOld = original.occurrenceLimitApplicable ? original.occurrenceLimitAmount : '';
  pushIfChanged('occurrenceLimitOverride', occurrenceNew, occurrenceOld);

  pushIfChanged('quotaShareTotalOverride', form.quotaShareTotal, original.quotaShareTotal);

  const retentionNew = form.retentionApplicable ? form.retentionAmount : '';
  const retentionOld = original.retentionApplicable ? original.retentionAmount : '';
  pushIfChanged('retentionOverride', retentionNew, retentionOld);

  if (mode === 'policy') {
    pushIfChanged('syndicateId', form.leadSyndicateId, original.leadSyndicateId);
    pushIfChanged('runoffOverride', form.runoff, original.runoff);
    pushIfChanged('sideAOverride', form.sideAValue, original.sideAValue);
    // carrierId holds the rInsurerGroup ID; the review screen maps it to the name via valueLabels.
    pushIfChanged('carrierOverride', form.carrierId, original.carrierId);
    pushIfChanged('policyPremiumOverride', form.premiumTotal, original.premiumTotal);
  }

  if (mode === 'line') {
    pushIfChanged('policyPremiumOverride', form.premiumTotal, original.premiumTotal);
  }

  return changes;
}

export type ValueLabelLookup = Record<string, Map<string, string>>;

function formatDiffValue(field: string, raw: unknown, lookups?: ValueLabelLookup): string {
  if (raw === undefined || raw === null || raw === '') return '';
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
  const strVal = String(raw);
  const lookup = lookups?.[field];
  if (lookup?.has(strVal)) return lookup.get(strVal)!;
  const label = policyFieldLabels[field];
  if (label?.formatter) {
    return label.formatter(strVal);
  }
  return strVal;
}

const savedRowKey = (kind: SubitemKind, itemId: number): string => `${kind}-saved-${itemId}`;

function stagedFor(state: RecordState, kind: SubitemKind, rowKey: string): StagedSubitem | undefined {
  return state.stagedSubitems.find((entry) => entry.kind === kind && entry.rowKey === rowKey);
}

// buildSubitemRows merges the saved sublimits/subretentions for a line with whatever the user has
// staged, so the panel renders one list: unchanged rows, edited rows, rows marked for removal, and
// rows added this session (which have no saved counterpart).
function buildSubitemRows(state: RecordState, kind: SubitemKind): SubitemRow[] {
  const saved = kind === 'sublimit' ? state.sublimits : state.subretentions;

  const rows: SubitemRow[] = saved.map((item) => {
    const rowKey = savedRowKey(kind, item.id);
    const staged = stagedFor(state, kind, rowKey);
    const basis = (item as PolicySubretention).basis ?? '';
    return {
      rowKey,
      kind,
      itemId: item.id,
      typeId: kind === 'sublimit' ? (item as PolicySublimit).sublimitTypeId : (item as PolicySubretention).subretentionTypeId,
      name: item.name,
      value: staged?.op === 'update' ? (staged.value ?? '') : item.value.toString(),
      basis: staged?.op === 'update' ? (staged.basis ?? '') : basis,
      savedValue: item.value,
      savedBasis: basis,
      originalValue: item.originalValue ?? undefined,
      status: (staged?.op === 'delete' ? 'deleted' : staged?.op === 'update' ? 'updated' : 'unchanged') as SubitemStatus,
    };
  });

  for (const staged of state.stagedSubitems) {
    if (staged.kind !== kind || staged.op !== 'add') continue;
    rows.push({
      rowKey: staged.rowKey,
      kind,
      typeId: staged.typeId!,
      name: staged.typeName ?? '',
      value: staged.value ?? '',
      basis: staged.basis ?? '',
      status: 'added',
    });
  }

  return rows;
}

function subitemDiffs(state: RecordState): DiffEntry[] {
  return state.stagedSubitems.map((staged) => {
    const label = `${subitemKindLabels[staged.kind]} — ${staged.typeName ?? ''}`;
    const savedList = staged.kind === 'sublimit' ? state.sublimits : state.subretentions;
    const savedItem = savedList.find((item) => item.id === staged.itemId);
    const savedValue = savedItem ? compactMoney(savedItem.value) : '';

    if (staged.op === 'add') {
      return { field: staged.rowKey, label, oldValue: '', newValue: formatSubitemValue(staged) };
    }
    if (staged.op === 'delete') {
      return { field: staged.rowKey, label, oldValue: savedValue, newValue: '' };
    }
    return { field: staged.rowKey, label, oldValue: savedValue, newValue: formatSubitemValue(staged) };
  });
}

function formatSubitemValue(staged: StagedSubitem): string {
  const amount = staged.value === '' || staged.value === undefined ? '' : compactMoney(Number(staged.value));
  if (staged.kind === 'subretention' && staged.basis) {
    return amount ? `${amount} (${staged.basis})` : staged.basis;
  }
  return amount;
}

interface SublimitOpPayload {
  operation: 'add' | 'update' | 'delete';
  policyLineId?: string;
  sublimitTypeId?: number;
  policySublimitId?: number;
  limitValue?: number;
}

interface SubretentionOpPayload {
  operation: 'add' | 'update' | 'delete';
  policyLineId?: string;
  subretentionTypeId?: number;
  policySubretentionId?: number;
  retentionValue?: number;
  retentionBasis?: string;
}

interface PolicyOverrideBatchPayload {
  policyOverride?: PolicyOverride;
  lineOverrides?: Record<string, PolicyLineOverride>;
  sublimitOps?: SublimitOpPayload[];
  subretentionOps?: SubretentionOpPayload[];
}

function toSublimitOp(staged: StagedSubitem, uniqLine?: string): SublimitOpPayload {
  if (staged.op === 'delete') {
    return { operation: 'delete', policySublimitId: staged.itemId };
  }
  const limitValue = Number(staged.value || 0);
  if (staged.op === 'update') {
    return { operation: 'update', policySublimitId: staged.itemId, limitValue };
  }
  return { operation: 'add', policyLineId: uniqLine, sublimitTypeId: staged.typeId, limitValue };
}

function toSubretentionOp(staged: StagedSubitem, uniqLine?: string): SubretentionOpPayload {
  if (staged.op === 'delete') {
    return { operation: 'delete', policySubretentionId: staged.itemId };
  }
  const retentionValue = Number(staged.value || 0);
  const retentionBasis = staged.basis || undefined;
  if (staged.op === 'update') {
    return { operation: 'update', policySubretentionId: staged.itemId, retentionValue, retentionBasis };
  }
  return { operation: 'add', policyLineId: uniqLine, subretentionTypeId: staged.typeId, retentionValue, retentionBasis };
}

export function usePolicyEditPending() {
  const states = reactive(new Map<RecordKey, RecordState>());
  // The last policy-wide fetch, kept so a record that learns its line after the fetch (a monoline
  // policy record, whose line arrives with its override) can still be given its rows.
  let fetchedSubitems: { sublimits: PolicySublimit[]; subretentions: PolicySubretention[] } | null = null;

  const caps: PolicyCaps = {
    carrierIdToName: new Map(),
    carrierNameToId: new Map(),
    allowCarrierUpdate: false,
    allowPremiumUpdate: false,
  };

  // Must be called (with reference data + policy source loaded) before loadRecord so toForm can
  // resolve the carrier name to its Select id, and before buildBatchPayload for the reverse.
  function configureCaps(opts: {
    carrierOptions: { label: string; value: string }[];
    allowCarrierUpdate: boolean;
    allowPremiumUpdate: boolean;
  }): void {
    caps.carrierIdToName = new Map(opts.carrierOptions.map((o) => [o.value, o.label]));
    caps.carrierNameToId = new Map(opts.carrierOptions.map((o) => [o.label.trim(), o.value]));
    caps.allowCarrierUpdate = opts.allowCarrierUpdate;
    caps.allowPremiumUpdate = opts.allowPremiumUpdate;
  }

  function init(refs: RecordRef[]) {
    states.clear();
    fetchedSubitems = null;
    for (const ref of refs) {
      states.set(ref.key, {
        ref: { ...ref },
        loaded: false,
        form: formSchema.parse({}),
        originalForm: formSchema.parse({}),
        validFields: allFieldsValid(),
        sublimits: [],
        subretentions: [],
        sublimitTypes: [],
        subretentionTypes: [],
        subitemTypesLoaded: false,
        stagedSubitems: [],
      });
    }
  }

  async function loadRecord(key: RecordKey): Promise<void> {
    const state = states.get(key);
    if (!state || state.loaded) return;

    if (state.ref.type === 'policy') {
      const res = await policyOverrideService.GetCurrentOverride(state.ref.uniqPolicy);
      const form = toForm(res.originalValues, res.policyOverride, caps.carrierNameToId);
      state.form = form;
      state.originalForm = structuredClone(form);
      state.loadedCarrierOverride = res.policyOverride.carrierOverride ?? undefined;
      if (res.policyLineId) {
        state.ref.uniqLine = String(res.policyLineId);
        assignSubitems(state);
      }
    } else {
      const res = await policyOverrideService.GetCurrentLineOverride(state.ref.uniqPolicy, state.ref.uniqLine!);
      const form = toForm(res.originalValues, res.policyOverride, caps.carrierNameToId);
      state.form = form;
      state.originalForm = structuredClone(form);
    }
    state.loaded = true;
  }

  let newSubitemCounter = 0;

  function assignSubitems(state: RecordState): void {
    if (!fetchedSubitems || !state.ref.uniqLine) return;
    const lineID = Number(state.ref.uniqLine);
    state.sublimits = fetchedSubitems.sublimits.filter((item) => item.policyLineId === lineID);
    state.subretentions = fetchedSubitems.subretentions.filter((item) => item.policyLineId === lineID);
  }

  // Sublimits and subretentions are fetched per policy, then fanned out to the records that own a
  // line — one round trip each rather than one per line.
  async function loadSubitems(policyID: string): Promise<void> {
    const [sublimits, subretentions] = await Promise.all([policyService.sublimits(policyID), policyService.subretentions(policyID)]);
    // An empty list can arrive as null; without this one empty kind would abort the fan-out and
    // blank the other kind on every line too.
    fetchedSubitems = { sublimits: sublimits ?? [], subretentions: subretentions ?? [] };
    states.forEach(assignSubitems);
  }

  // The selectable types are scoped server-side to the line's coverage type and already exclude
  // types the line carries; staged adds are excluded here so a type cannot be picked twice.
  async function loadSubitemTypes(key: RecordKey): Promise<void> {
    const state = states.get(key);
    if (!state || state.subitemTypesLoaded || !state.ref.uniqLine) return;

    const [sublimitTypes, subretentionTypes] = await Promise.all([
      policyService.sublimitTypes(state.ref.uniqPolicy, state.ref.uniqLine),
      policyService.subretentionTypes(state.ref.uniqPolicy, state.ref.uniqLine),
    ]);
    state.sublimitTypes = sublimitTypes;
    state.subretentionTypes = subretentionTypes;
    state.subitemTypesLoaded = true;
  }

  function subitemLineId(key: RecordKey): string | undefined {
    return states.get(key)?.ref.uniqLine;
  }

  function availableSubitemTypes(key: RecordKey, kind: SubitemKind): SubitemTypeRef[] {
    const state = states.get(key);
    if (!state) return [];
    const types = kind === 'sublimit' ? state.sublimitTypes : state.subretentionTypes;
    const stagedTypeIds = new Set(state.stagedSubitems.filter((entry) => entry.kind === kind && entry.op === 'add').map((entry) => entry.typeId));
    return types.filter((type) => !stagedTypeIds.has(type.id));
  }

  function subitemRows(key: RecordKey, kind: SubitemKind): SubitemRow[] {
    const state = states.get(key);
    return state ? buildSubitemRows(state, kind) : [];
  }

  function stageSubitemAdd(key: RecordKey, kind: SubitemKind, type: SubitemTypeRef): string {
    const state = states.get(key);
    if (!state) return '';
    const rowKey = `${kind}-new-${++newSubitemCounter}`;
    state.stagedSubitems.push({ kind, op: 'add', rowKey, typeId: type.id, typeName: type.name, value: '', basis: '' });
    return rowKey;
  }

  // matchesSavedSubitem compares an edit against the stored row rather than against the staged
  // op, so a value typed back to its original reads as unchanged however many edits preceded it.
  function matchesSavedSubitem(state: RecordState, kind: SubitemKind, itemId: number | undefined, value: string, basis: string): boolean {
    if (itemId === undefined) return false;
    const saved = (kind === 'sublimit' ? state.sublimits : state.subretentions).find((item) => item.id === itemId);
    if (!saved) return false;
    return value === saved.value.toString() && basis === ((saved as PolicySubretention).basis ?? '');
  }

  function setSubitemValue(key: RecordKey, kind: SubitemKind, rowKey: string, value: string, basis?: string): void {
    const state = states.get(key);
    if (!state) return;

    const staged = stagedFor(state, kind, rowKey);
    if (staged?.op === 'delete') return;

    if (staged) {
      staged.value = value;
      if (basis !== undefined) staged.basis = basis;
      // An update edited back to the saved value is no longer a change, so drop the op — otherwise
      // the row keeps its Edited badge and the record stays dirty. An add has nothing to revert to.
      if (staged.op === 'update' && matchesSavedSubitem(state, kind, staged.itemId, value, staged.basis ?? '')) {
        state.stagedSubitems = state.stagedSubitems.filter((entry) => !(entry.kind === kind && entry.rowKey === rowKey));
      }
      return;
    }

    const row = buildSubitemRows(state, kind).find((candidate) => candidate.rowKey === rowKey);
    if (!row?.itemId) return;

    const nextBasis = basis !== undefined ? basis : (row.savedBasis ?? '');
    if (matchesSavedSubitem(state, kind, row.itemId, value, nextBasis)) return;

    state.stagedSubitems.push({ kind, op: 'update', rowKey, itemId: row.itemId, typeId: row.typeId, typeName: row.name, value, basis: nextBasis });
  }

  function stageSubitemDelete(key: RecordKey, kind: SubitemKind, rowKey: string): void {
    const state = states.get(key);
    if (!state) return;

    const staged = stagedFor(state, kind, rowKey);
    // Removing a row added this session just drops the add; nothing was ever saved.
    if (staged?.op === 'add') {
      state.stagedSubitems = state.stagedSubitems.filter((entry) => entry !== staged);
      return;
    }

    const row = buildSubitemRows(state, kind).find((candidate) => candidate.rowKey === rowKey);
    if (!row?.itemId) return;

    // A pending edit on the row is superseded by the removal.
    state.stagedSubitems = state.stagedSubitems.filter((entry) => !(entry.kind === kind && entry.rowKey === rowKey));
    state.stagedSubitems.push({ kind, op: 'delete', rowKey, itemId: row.itemId, typeId: row.typeId, typeName: row.name });
  }

  function unstageSubitem(key: RecordKey, kind: SubitemKind, rowKey: string): void {
    const state = states.get(key);
    if (!state) return;
    state.stagedSubitems = state.stagedSubitems.filter((entry) => !(entry.kind === kind && entry.rowKey === rowKey));
  }

  function stagedSubitemCount(key: RecordKey, kind: SubitemKind): number {
    return states.get(key)?.stagedSubitems.filter((entry) => entry.kind === kind).length ?? 0;
  }

  function get(key: RecordKey): RecordState | undefined {
    return states.get(key);
  }

  function isDirty(key: RecordKey): boolean {
    const s = states.get(key);
    if (!s) return false;
    if (s.stagedSubitems.length > 0) return true;
    if (!s.loaded) return false;
    return !isEqual(s.form, s.originalForm);
  }

  const dirtyKeys = computed<RecordKey[]>(() => {
    const out: RecordKey[] = [];
    states.forEach((s, k) => {
      if (s.stagedSubitems.length > 0 || (s.loaded && !isEqual(s.form, s.originalForm))) {
        out.push(k);
      }
    });
    return out;
  });

  const totalFieldChanges = computed(() => {
    let count = 0;
    states.forEach((s) => {
      count += s.stagedSubitems.length;
      if (!s.loaded) return;
      count += diffPolicyFields(s.form, s.originalForm, s.ref.type).length;
    });
    return count;
  });

  function validate(key: RecordKey): boolean {
    const s = states.get(key);
    if (!s) return false;
    const parsed = formSchema.safeParse(s.form);
    s.validFields = allFieldsValid();
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        s.validFields[issue.path[0]?.toString() as keyof ValidFormFields] = false;
      }
    }
    return parsed.success;
  }

  function validateAll(): boolean {
    let ok = true;
    states.forEach((_, k) => {
      if (!validate(k)) ok = false;
    });
    return ok;
  }

  function buildDiffs(key: RecordKey, valueLabels?: ValueLabelLookup): DiffEntry[] {
    const s = states.get(key);
    if (!s) return [];
    const fieldDiffs = s.loaded
      ? diffPolicyFields(s.form, s.originalForm, s.ref.type).map((c) => ({
          field: c.field,
          label: policyFieldLabels[c.field]?.label ?? c.field,
          oldValue: formatDiffValue(c.field, c.oldValueRaw, valueLabels),
          newValue: formatDiffValue(c.field, c.newValueRaw, valueLabels),
        }))
      : [];
    return [...fieldDiffs, ...subitemDiffs(s)];
  }

  function buildBatchPayload(): PolicyOverrideBatchPayload {
    const payload: PolicyOverrideBatchPayload = {};
    const sublimitOps: SublimitOpPayload[] = [];
    const subretentionOps: SubretentionOpPayload[] = [];

    states.forEach((s) => {
      for (const staged of s.stagedSubitems) {
        if (staged.kind === 'sublimit') {
          sublimitOps.push(toSublimitOp(staged, s.ref.uniqLine));
        } else {
          subretentionOps.push(toSubretentionOp(staged, s.ref.uniqLine));
        }
      }
    });

    if (sublimitOps.length) payload.sublimitOps = sublimitOps;
    if (subretentionOps.length) payload.subretentionOps = subretentionOps;

    states.forEach((s) => {
      if (!s.loaded || isEqual(s.form, s.originalForm)) return;

      if (s.ref.type === 'policy') {
        const override = toPolicyOverride(s.form, caps);
        // Carrier resolution is lossy (a group resolves to its primary issuing company), so only
        // persist a carrier override when the user actually changed the dropdown; otherwise carry
        // the loaded override forward (possibly none) rather than normalizing the displayed carrier.
        if (caps.allowCarrierUpdate && s.form.carrierId === s.originalForm.carrierId) {
          override.carrierOverride = s.loadedCarrierOverride;
        }
        payload.policyOverride = override;
      } else if (s.ref.uniqLine) {
        if (!payload.lineOverrides) payload.lineOverrides = {};
        payload.lineOverrides[s.ref.uniqLine] = toPolicyLineOverride(s.form);
      }
    });

    return payload;
  }

  function resetOriginalsFromCurrent(): void {
    states.forEach((s) => {
      if (s.loaded) {
        // form is reactive; structuredClone can't clone proxies, so round-trip via JSON
        s.originalForm = JSON.parse(JSON.stringify(s.form)) as FormSchemeType;
      }
      // Staged subitem ops have been applied by the save, so clearing them stops a second save
      // replaying the same adds and deletes. The caller must follow with loadSubitems — the
      // stored rows have changed and sublimits/subretentions here are still the pre-save lists.
      s.stagedSubitems = [];
      s.subitemTypesLoaded = false;
    });
  }

  return {
    states,
    init,
    configureCaps,
    loadRecord,
    loadSubitems,
    loadSubitemTypes,
    subitemLineId,
    availableSubitemTypes,
    subitemRows,
    stageSubitemAdd,
    setSubitemValue,
    stageSubitemDelete,
    unstageSubitem,
    stagedSubitemCount,
    get,
    isDirty,
    dirtyKeys,
    totalFieldChanges,
    validate,
    validateAll,
    buildDiffs,
    buildBatchPayload,
    resetOriginalsFromCurrent,
  };
}
